import React, { useRef } from 'react';
import { connect } from 'react-redux';

import { toggleModal, updateMessages } from 'apps/auth/actions/appAction';
import { changeFormFieldValue } from 'apps/auth/actions/formAction';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import AccountPaymentMethod from 'apps/auth/components/paysendPlus/subscription/AccountPaymentMethod';
import AddNewCardModal from 'apps/auth/components/paysendPlus/subscription/AddNewCardModal';
import CardPaymentMethod from 'apps/auth/components/paysendPlus/subscription/CardPaymentMethod';
import CvvPopup from 'apps/auth/components/paysendPlus/subscription/CvvPopup';
import { CARD_TO_SUBSCRIPTION, GA_POCKET_TO_SUBSCRIPTION } from 'apps/auth/components/paysendPlus/utils';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import BasePage from 'apps/auth/components/utils/BasePage';
import { PLUS_SUBSCRIBE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PLUS_MAIN_PAGE, AUTH_PLUS_UPDATE_SUBSCRIBE_ACTION, URL_IMAGES_BANKS } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';

const PaysendPlusUpdatePaymentMethodPage = (props) => {
    const popupRef = useRef(null);
    const submitRef = useRef(null);
    const { linkedCards, accounts, subscriptionInfo, maxCardsLimit } = useExtra();
    const { cvvPopupTitle, paymentMethodsTitle, paysendAccountsTitle, linkedCardsTitle, addCard, defaultBankName, gotIt, dialogReachCardsLimit } =
        usePageMessages();

    const { fields } = useForm(PLUS_SUBSCRIBE_FORM_NAME);

    const showCvvPopup = (card) => {
        popupRef.current?.open(
            <CvvPopup
                formName={PLUS_SUBSCRIBE_FORM_NAME}
                title={resolveArguments(cvvPopupTitle, `${card?.bankName == null ? defaultBankName : card.bankName} ·· ${card.description}`)}
                submitRef={submitRef}
                onClick={sendCardInfo}
                popupRef={popupRef}
            />,
        );
    };

    const onAddNewClick = () => {
        if (maxCardsLimit) {
            popupRef.current?.open(
                <TextPopup
                    text={dialogReachCardsLimit}
                    popupRef={popupRef}
                    buttonText={gotIt}
                    onClick={() => {
                        popupRef.current?.close();
                    }}
                />,
            );
        } else {
            props.toggleModal(true, <AddNewCardModal postAction={''} popupRef={popupRef} closeModal={() => props.toggleModal(false)} />);
        }
    };

    const updateSubscriptionMethod = (paymentMethod, card = {}) => {
        fields.methodId.value = paymentMethod;
        if (fields.methodId.value === GA_POCKET_TO_SUBSCRIPTION) {
            fields.methodEncodedInfo.value = '';
            props.toggleSpinner(true);
            apiFetchData(AUTH_PLUS_UPDATE_SUBSCRIBE_ACTION, fields)
                .then((json) => {
                    if (json.messageBodyMessages) {
                        props.updateMessages(json.messageBodyMessages);
                    }
                    if (json && json.redirectTo) {
                        window.location.replace(json.redirectTo);
                    }
                })
                .finally(() => props.toggleSpinner(false))
                .catch((error) => {
                    props.toggleSpinner(false);
                    logWarn(error);
                });
        } else {
            fields.methodEncodedInfo.value = card.encryptedInfo;
            fields.cvv.value = '';
            showCvvPopup(card);
        }
    };

    const sendCardInfo = (fields) => {
        props.toggleSpinner(true);
        apiFetchData(AUTH_PLUS_UPDATE_SUBSCRIBE_ACTION, fields)
            .then((json) => {
                if (json.messageBodyMessages) {
                    props.updateMessages(json.messageBodyMessages);
                }
                if (json && json.redirectTo) {
                    window.location.replace(json.redirectTo);
                }
            })
            .finally(() => {
                props.toggleSpinner(false);
                props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, '', 'pMethodEncodedInfo');
                props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, '', 'cvv');
            })
            .catch((error) => {
                props.toggleSpinner(false);
                logWarn(error);
            });
    };

    return (
        <>
            <CommonPage backUrl={AUTH_PLUS_MAIN_PAGE} leftCloseIcon>
                <HeaderPanel title={paymentMethodsTitle} />
                {accounts?.length > 0 && (
                    <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                        {paysendAccountsTitle}
                    </Text>
                )}
                {accounts?.length > 0 &&
                    accounts.map((account) => {
                        return (
                            <AccountPaymentMethod
                                account={account}
                                key={account.id}
                                paymentMethod={GA_POCKET_TO_SUBSCRIPTION}
                                formName={PLUS_SUBSCRIBE_FORM_NAME}
                                tariff={subscriptionInfo}
                                popupRef={popupRef}
                                openClick={() => updateSubscriptionMethod(GA_POCKET_TO_SUBSCRIPTION)}
                            />
                        );
                    })}
                <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                    {linkedCardsTitle}
                </Text>
                <CardPaymentMethod name={addCard} icon="card-plus" onAddNewClick={onAddNewClick} />
                {Object.values(linkedCards)?.length > 0 &&
                    Object.values(linkedCards).map((card) => {
                        const name = `${card.bankName || defaultBankName} ·· ${card.description}`;
                        return (
                            <CardPaymentMethod
                                card={card}
                                name={name}
                                key={card.id}
                                id={card.id}
                                paymentMethod={CARD_TO_SUBSCRIPTION}
                                icon={!card.bankId ? 'card' : null}
                                src={card.bankId ? `${URL_IMAGES_BANKS}/${card.bankId}.png` : null}
                                diskVariant="tertiary"
                                popupRef={popupRef}
                                openClick={() => updateSubscriptionMethod(CARD_TO_SUBSCRIPTION, card)}
                            />
                        );
                    })}
            </CommonPage>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    toggleModal,
    changeFormFieldValue,
    updateMessages,
})(BasePage(PaysendPlusUpdatePaymentMethodPage));
